import dayjs from 'dayjs';
import { ToggleMylistReq } from '@/@types/MyList';
import { useFlutterConnection } from '@/composables/useFlutterConnection';
import { MylistItem } from '@/modules/MylistItem';
import Repository from './client/ApiAxiosClient';

export type MyListResponse = Omit<MylistItem, 'channel'> & {
  channel: {
    id: number;
    myBancolleId: number;
    isLike: number;
    isRemind: number;
    title: string;
    episord: string | null;
    onairAt: Date;
    detailUrl: string;
    channelSignatureType: number;
    name: string | null;
    channelSignatureName: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    channelType: 'tch1' | 'tch2';
    remindUserId: number;
  };
};

const TYPE_PROGRAM = 2;

export const useMylistRepository = () => {
  /**
   * - お気に入りリストに追加する
   * @param userId
   * @returns
   */
  const addItem = async (userId: number, program: ToggleMylistReq): Promise<MyListResponse[]> => {
    const requestData = {
      channel_signature_name: program.id,
      title: program.title,
      episord: Number(program.episode),
      onair_at: program.start ? dayjs(program.start).format('YYYY-MM-DD HH:mm:ss') : null,
      detail_url: program.page,
      channel_signature_type: TYPE_PROGRAM,
      name: null,
    };

    const { data } = await Repository.post(`/channel/${userId}`, requestData).catch((e) => {
      throw e;
    });

    return data;
  };

  /**
   * - お気に入りの状態を切り替える
   * @param userId
   * @param bancolleId
   * @returns
   */
  const toggleLike = async (userId: number, bancolleId: number) => {
    const { data } = await Repository.put(`/my-bancolle/${userId}/update-like/${bancolleId}`).catch((e) => {
      throw e;
    });
    return data;
  };

  /**
   * - 優先度更新
   */

  const updatePriority = async (bancolleId: number, userId: number, priority: number) => {
    const reqData = {
      myBancolles: [
        {
          id: bancolleId,
          user_id: userId,
          channel_signature_type: TYPE_PROGRAM,
          priority: priority,
        },
      ],
    };

    const { data } = await Repository.put(`/my-bancolle/${userId}/update-priority`, reqData).catch((e) => {
      throw e;
    });

    return data;
  };

  /**
   * - リマインドの状態を切り替える
   * @param userId
   * @param bancolleId
   * @returns
   */
  const toggleRemind = async (userId: number, bancolleId: number): Promise<MyListResponse[]> => {
    const { data } = await Repository.put(`my-bancolle/${userId}/update-remind/${bancolleId}`).catch((e) => {
      throw e;
    });
    return data;
  };

  /**
   * - お気に入りしたことのある全ての番組データを取得する
   * - アプリ起動時に、認証キーの取得が間に合わない可能性がある為、明示的に待つ
   * @param userId
   * @returns
   */
  const getAllProgramsMap = async (userId: number) => {
    const { data } = await Repository.get(`/my-bancolle/${userId}`).catch((e) => {
      throw e;
    });
    // 認証エラーの際は例外を投げる
    if (data.message) {
      throw new Error(data.message);
    }
    const programMap = new Map();

    data.forEach((item: MyListResponse) => {
      if (item.channelSignatureType === TYPE_PROGRAM) {
        const data: { id: number; liked: boolean; remind: boolean } = {
          id: item.id,
          liked: item.channel.isLike ? true : false,
          remind: item.channel.isRemind ? true : false,
        };
        programMap.set(item.channel.channelSignatureName, data);
      }
      return;
    });

    return programMap;
  };

  /**
   * - お気に入りした番組データを取得
   * @param {number} userId
   *
   */
  const getAllMyList = async (userId: number): Promise<MylistItem[]> => {
    const { data } = await Repository.get(`/my-bancolle/${userId}`).catch((e) => {
      throw e;
    });

    const result = data.flatMap((item: MyListResponse) => {
      if (item.channelSignatureType === TYPE_PROGRAM) {
        return new MylistItem(
          item.id,
          userId,
          item.channelSignatureType,
          item.priority,
          item.createdAt,
          item.updatedAt,
          item.deletedAt,
          item.bancolle,
          null,
          {
            id: item.channel.id,
            myBancolleId: item.channel.myBancolleId,
            isLike: item.channel.isLike,
            isRemind: item.channel.isRemind,
            title: 'Loading...',
            episord: item.channel.episord,
            onairAt: dayjs(item.channel.onairAt).toDate(),
            detailUrl: item.channel.detailUrl,
            channelSignatureType: item.channel.channelSignatureType,
            name: item.channel.name,
            channelSignatureName: item.channel.channelSignatureName,
            createdAt: item.channel.createdAt,
            updatedAt: item.channel.updatedAt,
            deletedAt: item.channel.deletedAt,
            channelType: item.channel.channelType,
            remindUserId: item.channel.remindUserId,
            thumbnail: `https://www.tbs.co.jp/tbs-ch/item/${item.channel.channelSignatureName}/thumbs/title-608.jpg`,
            description: null,
          }
        );
      } else {
        return [];
      }
    });

    return result;
  };

  /**
   * - お気に入りした番組を物理削除する
   * @param userId
   * @param bancolleId
   * @returns
   */
  const deleteItem = async (userId: number, bancolleId: number) => {
    const { data } = await Repository.delete(`/my-bancolle/${userId}/delete/${bancolleId}`).catch((e) => {
      throw e;
    });
    return data;
  };

  return { addItem, getAllProgramsMap, deleteItem, toggleLike, toggleRemind, getAllMyList, updatePriority };
};

export type MylistRepository = ReturnType<typeof useMylistRepository>;
